import { TodoForm } from "@/components/todos/TodoForm";
import { TodoList } from "@/components/todos/TodoList";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { JobApiResponse, TodoCreate, TodoResponse, TodoUpdate, useApiClient } from "@/lib/api";
import { sortJobsByPriorityAndRecency } from "@/lib/job-status";
import { AlertTriangle, CheckSquare } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

const Todos = () => {
  const { user } = useAuth();
  const { api } = useApiClient();
  
  const [todos, setTodos] = useState<TodoResponse[]>([]);
  const [jobs, setJobs] = useState<JobApiResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState<TodoResponse | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch todos and jobs
  const fetchData = useCallback(async () => {
    if (!user) return;
    
    setIsLoading(true);
    try {
      const [todosResponse, jobsResponse] = await Promise.all([
        api.getTodos(true), // Include completed todos
        api.getJobs()
      ]);
      setTodos(Array.isArray(todosResponse) ? todosResponse : []);
      setJobs(Array.isArray(jobsResponse) ? sortJobsByPriorityAndRecency(jobsResponse) : []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load todos');
      setTodos([]);
      setJobs([]);
    } finally {
      setIsLoading(false);
    }
  }, [api, user]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle todo completion toggle
  const handleToggleComplete = async (todoId: string, completed: boolean) => {
    try {
      const updatedTodo = await api.updateTodo(todoId, { completed });
      setTodos(prev => prev.map(todo => 
        todo.id === todoId ? updatedTodo : todo
      ));
      if (completed) {
        toast.success('Todo marked as complete!');
      } else {
        toast.success('Todo marked as incomplete');
      }
    } catch (error) {
      console.error('Failed to update todo:', error);
      toast.error('Failed to update todo');
    }
  };

  // Handle todo creation
  const handleCreateTodo = async (data: TodoCreate) => {
    setIsSubmitting(true);
    try {
      const newTodo = await api.createTodo(data);
      setTodos(prev => [newTodo, ...prev]);
      setIsFormOpen(false);
      toast.success('Todo created successfully!');
    } catch (error) {
      console.error('Failed to create todo:', error);
      toast.error('Failed to create todo');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle todo update
  const handleUpdateTodo = async (data: TodoUpdate) => {
    if (!editingTodo) return;
    setIsSubmitting(true);
    try {
      const updatedTodo = await api.updateTodo(editingTodo.id, data);
      setTodos(prev => prev.map(todo => 
        todo.id === editingTodo.id ? updatedTodo : todo
      ));
      setEditingTodo(undefined);
      setIsFormOpen(false);
      toast.success('Todo updated successfully!');
    } catch (error) {
      console.error('Failed to update todo:', error);
      toast.error('Failed to update todo');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle todo deletion
  const handleDeleteTodo = async (todoId: string) => {
    if (!confirm('Are you sure you want to delete this todo?')) return;
    try {
      await api.deleteTodo(todoId);
      setTodos(prev => prev.filter(todo => todo.id !== todoId));
      toast.success('Todo deleted successfully');
    } catch (error) {
      console.error('Failed to delete todo:', error);
      toast.error('Failed to delete todo');
    }
  };

  // Handle todo reordering (placeholder for drag-and-drop)
  const handleReorderTodos = async (todoIds: string[]) => {
    try {
      await api.reorderTodos(todoIds);
      // Optimistically update the order
      const reorderedTodos = todoIds.map(id => 
        todos.find(todo => todo.id === id)!
      ).filter(Boolean);
      setTodos(reorderedTodos);
    } catch (error) {
      console.error('Failed to reorder todos:', error);
      toast.error('Failed to reorder todos');
      // Refresh data on failure
      fetchData();
    }
  };

  // Handle opening create form
  const handleCreateNew = () => {
    setEditingTodo(undefined);
    setIsFormOpen(true);
  };

  // Handle opening edit form
  const handleEdit = (todo: TodoResponse) => {
    setEditingTodo(todo);
    setIsFormOpen(true);
  };

  // Handle closing form
  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingTodo(undefined);
  };

  // Handle form submission
  const handleFormSubmit = (data: TodoCreate | TodoUpdate) => {
    if (editingTodo) {
      handleUpdateTodo(data as TodoUpdate);
    } else {
      handleCreateTodo(data as TodoCreate);
    }
  };

  // Get summary stats
  const pendingCount = (todos ?? []).filter(todo => !todo.completed).length;
  const overdueCount = (todos ?? []).filter(todo => 
    !todo.completed && 
    todo.due_date && 
    new Date(todo.due_date) < new Date()
  ).length;

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6">
        {(todos ?? []).length === 0 ? (
          <div className="text-center py-12">
            <CheckSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Todos Yet</h3>
            <p className="text-gray-500 mb-6">
              Start by adding your first todo to organize your job search!
            </p>
            <Button onClick={handleCreateNew}>Add Your First Todo</Button>
          </div>
        ) : (
          <>
            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <CheckSquare className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="text-sm text-blue-600 font-medium">Total Todos</p>
                    <p className="text-2xl font-bold text-blue-900">{todos.length}</p>
                  </div>
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <CheckSquare className="h-8 w-8 text-yellow-600" />
                  <div>
                    <p className="text-sm text-yellow-600 font-medium">Pending</p>
                    <p className="text-2xl font-bold text-yellow-900">{pendingCount}</p>
                  </div>
                </div>
              </div>
              {overdueCount > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-8 w-8 text-red-600" />
                    <div>
                      <p className="text-sm text-red-600 font-medium">Overdue</p>
                      <p className="text-2xl font-bold text-red-900">{overdueCount}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* Todo List */}
            <TodoList
              todos={todos ?? []}
              jobs={jobs ?? []}
              onToggleComplete={handleToggleComplete}
              onEdit={handleEdit}
              onDelete={handleDeleteTodo}
              onReorder={handleReorderTodos}
              onCreateNew={handleCreateNew}
              isLoading={isLoading}
            />
          </>
        )}
        {/* Todo Form Dialog */}
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <TodoForm
              todo={editingTodo}
              jobs={jobs ?? []}
              onSubmit={handleFormSubmit}
              onCancel={handleCloseForm}
              isLoading={isSubmitting}
              embedded
            />
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Todos;
